import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { browseMovies, getCategories, getCategory, getHome, getMovie, getSimilar } from "@/services/movieService";

const LONG = 10 * 60 * 1000;

export const useHome = () => useQuery({ queryKey: ["home"], queryFn: ({ signal }) => getHome({ signal }), staleTime: 60 * 1000 });

export function useMovie(id) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["movie", id],
    queryFn: ({ signal }) => getMovie(id, { signal }),
    enabled: Boolean(id),
    staleTime: LONG,
    retry: (count, err) => err?.status !== 404 && count < 2,
    placeholderData: () => qc.getQueryData(["movie-preview", id]),
  });
}

export const useSimilar = (id) => useQuery({ queryKey: ["similar", id], queryFn: ({ signal }) => getSimilar(id, { signal }), enabled: Boolean(id), staleTime: LONG });

export const useCategories = () => useQuery({ queryKey: ["genres"], queryFn: ({ signal }) => getCategories({ signal }), staleTime: LONG });

export function useBrowse({ type, genre, sort }) {
  return useInfiniteQuery({
    queryKey: ["browse", type || "all", genre || "", sort || "popular"],
    queryFn: ({ pageParam, signal }) => browseMovies({ page: pageParam, type, genre, sort, signal }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCategory(slug, sort) {
  return useInfiniteQuery({
    queryKey: ["category", slug, sort],
    queryFn: ({ pageParam, signal }) => getCategory(slug, { page: pageParam, sort, signal }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled: Boolean(slug),
    staleTime: 2 * 60 * 1000,
    retry: (count, err) => err?.status !== 404 && count < 2,
  });
}

/** Seed the detail cache with card data so the detail page renders instantly. */
export function usePrimeMovie() {
  const qc = useQueryClient();
  return (movie) => {
    if (movie && !qc.getQueryData(["movie", movie.id])) qc.setQueryData(["movie-preview", movie.id], movie);
  };
}
